import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  LEGAL_OPERATOR, LEGAL_CONTACT_EMAIL, LEGAL_JURISDICTION,
  LEGAL_LAST_UPDATED, LEGAL_EFFECTIVE_DATE, APP_NAME, APP_WEB_URL,
} from "@/lib/legal";

// ─────────────────────────────────────────────────────────────────────
// Nova — Terms of Service (launch-grade)
// Structure covers all launch-blocking clauses:
//   • Acceptance & eligibility
//   • Account security
//   • Acceptable use + prohibited conduct
//   • Subscription auto-renewal (App Store 3.1.2 formatted)
//   • Cancellation, refunds, price changes
//   • IP & user content licence
//   • DMCA takedown process
//   • AI-output & health disclaimers
//   • Warranty disclaimer & limitation of liability
//   • Indemnification
//   • Governing law & dispute resolution
//   • Arbitration & class-action waiver
//   • Termination
//   • Severability, entire agreement, force majeure, notices
//   • Third-party terms (Apple / Google App Store)
// ─────────────────────────────────────────────────────────────────────

function Section({ title, children, id }) {
  return (
    <section id={id} style={{ marginBottom: 24 }}>
      <h3 className="h3" style={{ color: "var(--text)", marginBottom: 10, fontSize: 16 }}>{title}</h3>
      <div className="body-sm" style={{ color: "var(--text-dim)", lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

const listStyle = { marginTop: 8, paddingLeft: 18 };
const boxStyle = {
  marginTop: 10,
  padding: 12,
  borderRadius: 10,
  border: "1px solid var(--gold, #b28a3e)",
  background: "rgba(178,138,62,0.08)",
  fontSize: 13,
  lineHeight: 1.6,
};

export default function TermsScreen() {
  const navigate = useNavigate();
  const mailto = `mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent("Terms question — Nova")}`;

  return (
    <div className="screen fade-in" data-testid="terms-screen" style={{ paddingBottom: 60 }}>
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back" data-testid="terms-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">TERMS</div>
        <span style={{ width: 32 }} />
      </div>

      <div className="eyebrow" style={{ marginTop: 16, marginBottom: 8 }}>
        LEGAL · EFFECTIVE {LEGAL_EFFECTIVE_DATE.toUpperCase()} · LAST UPDATED {LEGAL_LAST_UPDATED.toUpperCase()}
      </div>
      <h2 className="h1" style={{ fontSize: 30, marginBottom: 18, lineHeight: 1.12 }}>Terms of Service</h2>

      <Section title="Summary (plain English)">
        <p>
          By using {APP_NAME}, you agree to these Terms and our Privacy Policy. You must be
          at least 13 (16 in the EU/UK). Subscriptions auto-renew until you cancel from
          Settings. AI-generated recommendations are informational — not medical advice.
          We provide the app &quot;as-is&quot; and cap our liability at fees paid in the prior
          12 months. Disputes are resolved in {LEGAL_JURISDICTION}.
        </p>
      </Section>

      <Section title="1. Acceptance and binding agreement">
        <p>
          These Terms of Service (&quot;Terms&quot;) form a binding legal agreement between
          you and <strong>{LEGAL_OPERATOR}</strong> (&quot;Operator&quot;, &quot;we&quot;,
          &quot;us&quot;), the operator of {APP_NAME} (&quot;the app&quot;, &quot;the
          service&quot;). By creating an account, accessing, or using the service, you
          confirm that you have read, understood, and agreed to these Terms and to our
          Privacy Policy. If you do not agree, do not use the service.
        </p>
      </Section>

      <Section title="2. Who we are">
        <p>
          {APP_NAME} is operated by <strong>{LEGAL_OPERATOR}</strong> ({LEGAL_JURISDICTION}). 
          Reachable at <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="3. Eligibility">
        <p>
          You must be at least 13 years old (or 16 in the European Union, European Economic
          Area, or United Kingdom) to use {APP_NAME}. If you are under the age of majority in
          your jurisdiction, you may use the service only with the involvement of a parent
          or legal guardian. By using the service you represent that you meet these criteria
          and have the legal capacity to enter this agreement.
        </p>
      </Section>

      <Section title="4. Your account">
        <ul className="bullet-list" style={listStyle}>
          <li>You must provide accurate registration information and keep your credentials confidential.</li>
          <li>You are responsible for all activity that occurs under your account.</li>
          <li>Notify us immediately at <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a> if you suspect unauthorised access.</li>
          <li>One account per person. You may not share, sell, or transfer your account.</li>
        </ul>
      </Section>

      <Section title="5. Acceptable use">
        <p>You agree NOT to:</p>
        <ul className="bullet-list" style={listStyle}>
          <li>Upload photos of any person other than yourself without their explicit consent.</li>
          <li>Submit content that is illegal, harmful, harassing, defamatory, hateful, or sexually explicit.</li>
          <li>Attempt to reverse-engineer, decompile, scrape, or otherwise abuse the service or its APIs.</li>
          <li>Use automated systems (bots, scrapers) without our written permission.</li>
          <li>Introduce malware, viruses, or attempt to gain unauthorised access to our systems or other users&apos; accounts.</li>
          <li>Use AI outputs to make consequential medical, dietary, mental-health, or fitness decisions without consulting a qualified professional.</li>
          <li>Resell, sublicense, or commercially redistribute {APP_NAME} features, output, or content.</li>
          <li>Misrepresent your identity, age, or affiliation.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          We may suspend or terminate accounts that violate these rules, with or without notice.
        </p>
      </Section>

      <Section title="6. Subscriptions, billing, and auto-renewal">
        <div style={boxStyle}>
          <strong style={{ color: "var(--gold)" }}>Auto-renewal disclosure (required):</strong>
          <ul className="bullet-list" style={{ ...listStyle, marginTop: 6 }}>
            <li>{APP_NAME} Premium is sold as an <strong>auto-renewing subscription</strong> at the price displayed at checkout — currently $4.99 / month or $39.99 / year (prices in USD, may vary by region).</li>
            <li>Payment is charged to your Stripe-provided payment method (or, on iOS in-app purchases, to your Apple ID) at confirmation of purchase.</li>
            <li>The subscription <strong>automatically renews</strong> at the end of each billing period unless you cancel at least 24 hours before the end of the current period.</li>
            <li>Your account is charged for renewal within 24 hours prior to the end of the current period.</li>
            <li>You can manage or cancel your subscription at any time from <em>Settings → Billing &amp; invoices</em> — this opens the Stripe Customer Portal where you can cancel, update your card, or download invoices.</li>
            <li>If you purchased through the Apple App Store, cancellation is handled in your device Settings → Apple ID → Subscriptions.</li>
            <li>Cancellation takes effect at the end of the current paid period. You retain access to Premium until that date.</li>
          </ul>
        </div>
        <p style={{ marginTop: 12 }}>Additional billing terms:</p>
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Refunds:</strong> outside of statutory rights, we do not offer refunds for partial billing periods. EU/UK consumers have a 14-day right of withdrawal under the Consumer Rights Directive, which you waive by starting to use Premium features within that period (see the checkout consent notice).</li>
          <li><strong>Failed payments:</strong> if a renewal fails, we will keep Premium active for a short grace period and email you. If the payment is not recovered, Premium will be paused until you update your payment method.</li>
          <li><strong>Price changes:</strong> we will notify you by email at least 30 days before any price increase applies to your next renewal. You may cancel before the new price takes effect.</li>
          <li><strong>Promotional / free trials:</strong> if offered, trials auto-convert to paid subscriptions at the end of the trial unless cancelled. Only one trial per person.</li>
          <li><strong>Taxes:</strong> prices exclude applicable sales tax or VAT unless stated otherwise. Where required, tax will be added at checkout.</li>
        </ul>
      </Section>

      <Section title="7. Free-tier and feature limits">
        <p>
          The free tier is provided at our discretion and may be changed, limited, or removed
          at any time. Certain features (e.g. daily scan quota, saved outfits, personalised
          coach) may be gated to Premium accounts. We will not remove or reduce features
          you have already paid for during the current subscription period.
        </p>
      </Section>

      <Section title="8. AI-generated recommendations">
        <p>
          {APP_NAME} uses third-party AI models (currently OpenAI GPT and Anthropic Claude
          families, and Google Gemini) to generate coaching text, meal analyses, skin/hair
          recommendations, and style suggestions. AI output may:
        </p>
        <ul className="bullet-list" style={listStyle}>
          <li>Contain errors, omissions, or occasional &quot;hallucinations&quot;.</li>
          <li>Reflect biases present in the underlying training data.</li>
          <li>Vary between requests even for identical prompts.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          We do <strong>not</strong> warrant the accuracy, completeness, or fitness of AI
          outputs for any particular purpose. Use them as inspiration and cross-check
          anything important with a qualified professional.
        </p>
      </Section>

      <Section title="9. Health & medical disclaimer">
        <p>
          {APP_NAME} provides general wellness, fitness, nutrition, skincare, and grooming
          information. <strong>It is not medical advice</strong> and is not intended to
          diagnose, treat, cure, or prevent any disease. Consult a licensed physician,
          registered dietitian, or dermatologist before making changes to your diet,
          exercise, medication, or skincare routine — especially if you are pregnant,
          breastfeeding, under 18, or managing a medical condition. In an emergency,
          call your local emergency number, not the app.
        </p>
      </Section>

      <Section title="10. Intellectual property">
        <p>
          The {APP_NAME} name, logo, app code, designs, and all content we provide (other
          than user content) remain the exclusive property of {LEGAL_OPERATOR} and are
          protected by copyright, trademark, and other laws. Nothing in these Terms grants
          you a licence to use them beyond the personal, non-commercial use required to
          operate the app.
        </p>
      </Section>

      <Section title="11. Your content and licence to us">
        <p>
          You retain ownership of the photos, notes, meal logs, and other content you submit
          (&quot;User Content&quot;). By submitting User Content, you grant {APP_NAME} a
          worldwide, royalty-free, non-exclusive licence to store, reproduce, and process
          it solely to provide the service to you (including transmitting relevant portions
          to our AI sub-processors as described in the Privacy Policy). This licence ends
          when you delete the content or your account.
        </p>
        <p style={{ marginTop: 8 }}>
          You represent and warrant that you own or have the necessary rights to submit
          your User Content and that it does not infringe any third party&apos;s rights.
        </p>
      </Section>

      <Section title="12. Community feed and user-generated content">
        <p>
          If {APP_NAME} offers a community feed, posts you share there are visible to other
          users. You must follow our&nbsp;
          <a href="/legal/community-guidelines" style={{ color: "var(--gold)" }}>Community Guidelines</a>
          &nbsp;— we may remove any post at our discretion if it violates them. Every post
          includes report/block controls; a post that receives 3 or more distinct reports
          is automatically hidden pending review. You are responsible for your public
          posts and must not share other people&apos;s images or personal information
          without their consent.
        </p>
      </Section>

      <Section title="13. DMCA / copyright takedowns">
        <p>
          We respect intellectual property rights. If you believe content on {APP_NAME}
          infringes your copyright, send a written notice to&nbsp;
          <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a> including:
        </p>
        <ul className="bullet-list" style={listStyle}>
          <li>Your physical or electronic signature.</li>
          <li>Identification of the copyrighted work claimed to have been infringed.</li>
          <li>Identification of the material claimed to be infringing (URL or specific description) sufficient for us to locate it.</li>
          <li>Your contact information (address, phone, email).</li>
          <li>A statement that you have a good-faith belief the use is not authorised by the copyright owner, its agent, or the law.</li>
          <li>A statement, under penalty of perjury, that the information is accurate and you are authorised to act on the owner&apos;s behalf.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          We may terminate accounts of repeat infringers. We will forward counter-notices
          per 17 U.S.C. § 512(g).
        </p>
      </Section>

      <Section title="14. Termination">
        <p>
          You may delete your account at any time from <em>Settings → Delete account</em> —
          all associated data is erased per our Privacy Policy.
        </p>
        <p style={{ marginTop: 8 }}>
          We may suspend or terminate your access to the service immediately, with or
          without notice, if you breach these Terms, engage in fraud, or if we are required
          to do so by law. Sections that by their nature survive termination
          (IP, liability, indemnity, disputes) shall continue in effect.
        </p>
      </Section>

      <Section title="15. Warranty disclaimer">
        <p>
          {APP_NAME.toUpperCase()} IS PROVIDED <strong>&quot;AS IS&quot; AND &quot;AS
          AVAILABLE&quot;</strong>, WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING (WITHOUT LIMITATION) IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND UNINTERRUPTED OR ERROR-FREE
          OPERATION. WE DO NOT WARRANT THAT AI-GENERATED CONTENT WILL BE ACCURATE.
          SOME JURISDICTIONS DO NOT ALLOW EXCLUSION OF CERTAIN WARRANTIES, SO PARTS OF
          THIS SECTION MAY NOT APPLY TO YOU.
        </p>
      </Section>

      <Section title="16. Limitation of liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, {LEGAL_OPERATOR.toUpperCase()} AND OUR
          SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
          DATA, USE, OR GOODWILL, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
          NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT WE HAVE
          BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p style={{ marginTop: 8 }}>
          OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE
          TERMS OR THE SERVICE IS LIMITED TO THE GREATER OF (A) THE AMOUNTS YOU PAID US
          IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) USD $50.
        </p>
        <p style={{ marginTop: 8 }}>
          NOTHING IN THESE TERMS LIMITS LIABILITY THAT CANNOT BE LIMITED UNDER APPLICABLE
          LAW (E.G. LIABILITY FOR FRAUD, GROSS NEGLIGENCE, OR DEATH/PERSONAL INJURY CAUSED
          BY OUR NEGLIGENCE).
        </p>
      </Section>

      <Section title="17. Indemnification">
        <p>
          You agree to indemnify, defend, and hold harmless {LEGAL_OPERATOR} and our service
          providers from any claim, damages, liabilities, and expenses (including reasonable
          legal fees) arising out of (a) your use of the service, (b) your User Content,
          (c) your breach of these Terms, or (d) your violation of any law or third-party
          right.
        </p>
      </Section>

      <Section title="18. Governing law and dispute resolution">
        <p>
          These Terms are governed by the laws of <strong>{LEGAL_JURISDICTION}</strong>,
          without regard to its conflict-of-laws principles. Any dispute arising out of or
          relating to these Terms or the service will be resolved exclusively in the state
          or federal courts located in {LEGAL_JURISDICTION}, unless mandatory consumer
          protection laws of your country of residence provide otherwise. You consent to
          the personal jurisdiction of those courts and waive any objection to that venue.
        </p>
      </Section>

      <Section title="19. Class-action waiver (US users)">
        <p>
          If you reside in the United States, you and Operator agree that any dispute will
          be resolved on an individual basis. <strong>You waive any right to participate in
          a class action, class arbitration, or representative action against Operator.</strong>
          A court may sever this waiver if it is found unenforceable in a specific case,
          in which case the remainder of Section 18 will apply.
        </p>
      </Section>

      <Section title="20. Third-party app-store terms">
        <p>
          If you obtained {APP_NAME} through the Apple App Store, you acknowledge that
          these Terms are between you and {LEGAL_OPERATOR} only, and not with Apple.
          Apple is not responsible for the app or its content. However, Apple is a
          third-party beneficiary of these Terms and may enforce them against you.
          Apple&apos;s Standard EULA applies to your use of the app on iOS to the extent
          not superseded by these Terms.
        </p>
        <p style={{ marginTop: 8 }}>
          Similarly, if obtained through Google Play, Google is not a party to these Terms
          and Google Play&apos;s terms apply where relevant.
        </p>
      </Section>

      <Section title="21. Notices and electronic communications">
        <p>
          You consent to receive communications from us electronically, including via
          email (to the address on your account) and via in-app messages. These satisfy
          any legal requirement that a communication be in writing.
        </p>
      </Section>

      <Section title="22. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be announced
          in-app and by email at least 30 days before they take effect (or sooner if
          required by law). Your continued use of the service after the effective date
          constitutes acceptance. If you do not agree, you should stop using the service
          and, if applicable, cancel your subscription.
        </p>
      </Section>

      <Section title="23. Force majeure">
        <p>
          Neither party is liable for failure or delay in performance caused by events
          beyond reasonable control, including natural disasters, war, terrorism, labour
          disputes, government action, or failures of third-party networks or hosting
          providers.
        </p>
      </Section>

      <Section title="24. Severability, waiver, entire agreement">
        <p>
          If any provision of these Terms is found unenforceable, the remaining provisions
          remain in effect. Our failure to enforce any right is not a waiver of that right.
          These Terms, together with our Privacy Policy, constitute the entire agreement
          between you and Operator regarding the service and supersede any prior
          agreements.
        </p>
      </Section>

      <Section title="25. Assignment">
        <p>
          You may not assign these Terms without our prior written consent. We may assign
          them to an affiliate or in connection with a merger, acquisition, or sale of
          assets — in which case we will notify you.
        </p>
      </Section>

      <Section title="26. Contact">
        <p>
          Questions about these Terms:&nbsp;
          <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a>
        </p>
        <p style={{ marginTop: 6 }}>
          Operator: {LEGAL_OPERATOR}, {LEGAL_JURISDICTION}. App: {APP_WEB_URL}
        </p>
      </Section>

      <div className="tiny" style={{ marginTop: 30, textAlign: "center", color: "var(--text-faint)", lineHeight: 1.55 }}>
        Effective {LEGAL_EFFECTIVE_DATE}. Last updated {LEGAL_LAST_UPDATED}.
      </div>
    </div>
  );
}
